// store/slices/domainSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as domainApi from '../../api/domainApi';
import { EmailDomain, GoDaddyDomain, SubdomainConfig } from '../../api/domainApi';

interface DomainState {
    emailDomains: EmailDomain[];
    godaddyDomains: GoDaddyDomain[];
    loading: boolean;
    error: string | null;
}

const initialState: DomainState = {
    emailDomains: [],
    godaddyDomains: [],
    loading: false,
    error: null
};

// Async thunks for domain operations
export const fetchEmailDomains = createAsyncThunk(
    'domains/fetchEmailDomains',
    async (_, { rejectWithValue }) => {
        try {
            const response = await domainApi.getAllEmailDomains();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch email domains');
        }
    }
);

export const fetchGoDaddyDomains = createAsyncThunk(
    'domains/fetchGoDaddyDomains',
    async (_, { rejectWithValue }) => {
        try {
            const response = await domainApi.getAllGoDaddyDomains();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch GoDaddy domains');
        }
    }
);

export const addEmailDomain = createAsyncThunk(
    'domains/addEmailDomain',
    async (domain: string, { rejectWithValue }) => {
        try {
            const response = await domainApi.addEmailDomain(domain);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to add email domain');
        }
    }
);

export const deleteEmailDomain = createAsyncThunk(
    'domains/deleteEmailDomain',
    async (domain: string, { rejectWithValue }) => {
        try {
            const response = await domainApi.deleteEmailDomain(domain);
            return { domain, ...response.data };
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete email domain');
        }
    }
);

export const checkEmailDomainStatus = createAsyncThunk(
    'domains/checkEmailDomainStatus',
    async (domain: string, { rejectWithValue }) => {
        try {
            const response = await domainApi.checkEmailDomainStatus(domain);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to check email domain status');
        }
    }
);

// New thunk for creating subdomains
export const createSubdomain = createAsyncThunk(
    'domains/createSubdomain',
    async (config: SubdomainConfig, { rejectWithValue }) => {
        try {
            const response = await domainApi.createSubdomain(config);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create subdomain');
        }
    }
);

// Domain slice
const domainSlice = createSlice({
    name: 'domains',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Fetch email domains
            .addCase(fetchEmailDomains.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchEmailDomains.fulfilled, (state, action) => {
                state.loading = false;
                state.emailDomains = action.payload;
            })
            .addCase(fetchEmailDomains.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Fetch GoDaddy domains
            .addCase(fetchGoDaddyDomains.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGoDaddyDomains.fulfilled, (state, action) => {
                state.loading = false;
                state.godaddyDomains = action.payload;
            })
            .addCase(fetchGoDaddyDomains.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Add email domain
            .addCase(addEmailDomain.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(addEmailDomain.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(addEmailDomain.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Delete email domain
            .addCase(deleteEmailDomain.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteEmailDomain.fulfilled, (state, action) => {
                state.loading = false;
                state.emailDomains = state.emailDomains.filter(
                    (domain) => domain.name !== action.payload.domain
                );
            })
            .addCase(deleteEmailDomain.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Check email domain status
            .addCase(checkEmailDomainStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(checkEmailDomainStatus.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(checkEmailDomainStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Create subdomain
            .addCase(createSubdomain.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createSubdomain.fulfilled, (state) => {
                state.loading = false;
            })
            .addCase(createSubdomain.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

export default domainSlice.reducer;